import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { BasePage } from "components/BasePage/BasePage";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import { useUpdateProjectPayload } from "hooks/useProjects";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";

interface NotifiedBodiesRouteParams {
  projectId: string;
}

export const NotifiedBodies = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<NotifiedBodiesRouteParams>();
  const { project } = useProjectDetails(projectId);
  const [notifiedBodyName, setNotifiedBodyName] = useState("");
  const [notifiedBodyNumber, setNotifiedBodyNumber] = useState("");
  const [notifiedBodyAddress, setNotifiedBodyAddress] = useState("");
  const [notifiedBodyEmail, setNotifiedBodyEmail] = useState("");
  const { updateProject } = useUpdateProjectPayload(
    projectId,
    t("notification.saving.set_notified_body_success"),
    `/projects/${projectId}?saved=true`
  );

  useEffect(() => {
    if (project?.notifiedBody?.name) {
      setNotifiedBodyName(project.notifiedBody.name);
    }
    if (project?.notifiedBody?.number) {
      setNotifiedBodyNumber(project.notifiedBody.number);
    }
    if (project?.notifiedBody?.address) {
      setNotifiedBodyAddress(project.notifiedBody.address);
    }
    if (project?.notifiedBody?.email) {
      setNotifiedBodyEmail(project.notifiedBody.email);
    }
  }, [project]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateProject({
      ...project!,
      notifiedBody: {
        ...project!.notifiedBody,
        required: true,
        name: notifiedBodyName,
        email: notifiedBodyEmail,
        number: notifiedBodyNumber,
        address: notifiedBodyAddress,
      },
    });
  };

  return (
    <BasePage
      breadcrumbItems={[
        { label: "Dashboard", location: "/dashboard" },
        {
          label: project?.projectdata?.product_name || projectId,
          location: `/projects/${projectId}`,
        },
        {
          label: t("notifiedBodies.label"),
          location: `/projects/${projectId}/notifiedBodies`,
        },
      ]}
    >
      <div>
        <div className="pb-5">
          <h3 className="text-3xl leading-6 font-medium text-gray-900">
            <Trans i18nKey={"notifiedBodies.label"}></Trans>
          </h3>
          <p className="text-sm text-gray-500 block pt-8 w-full">
            <Trans i18nKey={"notifiedBodies.subline"}></Trans>
          </p>
        </div>

        <div className="mt-6 space-y-6">
          {/* Link to EUR-Lex */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-3">
              {t("notifiedBodies.find.label")}
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              <Trans i18nKey={"notifiedBodies.find.description"}></Trans>
            </p>
            <a
              href="https://webgate.ec.europa.eu/single-market-compliance-space/notified-bodies/notified-body-list?filter=legislationId:162400,notificationStatusId:1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t("notifiedBodies.find.link")}
              <ExternalLinkIcon className="ml-2 h-4 w-4" aria-hidden="true" />
            </a>
          </div>

          {/* Input Form */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {t("notifiedBodies.add.label")}
            </h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="notifiedBodyName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.name")}
                </label>
                <input
                  type="text"
                  id="notifiedBodyName"
                  name="notifiedBodyName"
                  value={notifiedBodyName}
                  onChange={(e) => setNotifiedBodyName(e.target.value)}
                  placeholder={t("notifiedBodies.add.placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyNumber"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.number")}
                </label>
                <input
                  type="text"
                  id="notifiedBodyNumber"
                  name="notifiedBodyNumber"
                  value={notifiedBodyNumber}
                  onChange={(e) => setNotifiedBodyNumber(e.target.value)}
                  placeholder={t("notifiedBodies.add.number_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyAddress"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.address")}
                </label>
                <input
                  type="text"
                  id="notifiedBodyAddress"
                  name="notifiedBodyAddress"
                  value={notifiedBodyAddress}
                  onChange={(e) => setNotifiedBodyAddress(e.target.value)}
                  placeholder={t("notifiedBodies.add.address_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyEmail"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.email")}
                </label>
                <input
                  type="email"
                  id="notifiedBodyEmail"
                  name="notifiedBodyEmail"
                  value={notifiedBodyEmail}
                  onChange={(e) => setNotifiedBodyEmail(e.target.value)}
                  placeholder={t("notifiedBodies.add.email_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="pt-9 text-right flex justify-end items-center">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!notifiedBodyName}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("notifiedBodies.add.save")}
          </button>
        </div>
      </div>
    </BasePage>
  );
};

export default NotifiedBodies;
